import { useEffect, useState } from 'react';
import { Select } from '@/components/ui';

/**
 * The period picker and the auto-refresh switch, shared by both dashboards.
 *
 * One component rather than two copies: these two controls sit together, and
 * a client dashboard that refreshes on a different cadence to the admin one —
 * or offers a period the other does not — is the kind of difference nobody
 * decides on and everybody notices.
 */

export const WINDOW_OPTIONS = [
  { key: 'today', label: 'Today' },
  { key: '24h', label: 'Last 24 hours' },
  { key: '3d', label: 'Last 3 days' },
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: 'last_month', label: 'Last month' },
] as const;

export type WindowKey = (typeof WINDOW_OPTIONS)[number]['key'];

export const REFRESH_MS = 5_000;

const WINDOW_STORE = 'glee.dashboard.window';
const REFRESH_STORE = 'glee.dashboard.autorefresh';

function read(key: string, fallback: string): string {
  try {
    return window.localStorage.getItem(key) ?? fallback;
  } catch {
    // Private windows and blocked site data both throw here. A remembered
    // preference is a convenience; losing it must not stop the page.
    return fallback;
  }
}

function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* see above */
  }
}

/** The chosen period, remembered between visits. */
export function useDashboardWindow(): [WindowKey, (next: WindowKey) => void] {
  const [key, setKey] = useState<WindowKey>(
    () => read(WINDOW_STORE, 'today') as WindowKey,
  );
  const set = (next: WindowKey) => {
    setKey(next);
    write(WINDOW_STORE, next);
  };
  return [key, set];
}

/**
 * Whether to poll, and how often.
 *
 * Returns `false` rather than a number when it is off, which is what
 * react-query's `refetchInterval` wants.
 *
 * Polling STOPS while the tab is hidden. A dashboard left open in a
 * background tab overnight is twelve thousand requests nobody will ever look
 * at, and on a four-core box shared with live calls that is not free.
 */
export function useAutoRefresh(): {
  on: boolean;
  setOn: (next: boolean) => void;
  interval: number | false;
} {
  const [on, setOnState] = useState(() => read(REFRESH_STORE, 'on') === 'on');
  const [visible, setVisible] = useState(
    () => typeof document === 'undefined' || !document.hidden,
  );

  useEffect(() => {
    const onChange = () => setVisible(!document.hidden);
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);

  const setOn = (next: boolean) => {
    setOnState(next);
    write(REFRESH_STORE, next ? 'on' : 'off');
  };

  return { on, setOn, interval: on && visible ? REFRESH_MS : false };
}

export function DashboardControls({
  window: windowKey,
  onWindow,
  autoRefresh,
  onAutoRefresh,
  updatedAt,
  extra,
}: {
  window: WindowKey;
  onWindow: (next: WindowKey) => void;
  autoRefresh: boolean;
  onAutoRefresh: (next: boolean) => void;
  /** When the data last arrived, so a paused dashboard is not mistaken for a live one. */
  updatedAt?: number;
  extra?: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {extra}

      <label
        className="flex select-none items-center gap-1.5 rounded-md border border-line bg-surface px-2.5 py-1.5 text-xs text-ink-600 dark:text-ink-300"
        title={
          autoRefresh
            ? 'Refreshing every 5 seconds. Pauses while this tab is in the background.'
            : 'Paused. The numbers are from when the page last loaded.'
        }
      >
        <input
          type="checkbox"
          checked={autoRefresh}
          onChange={(e) => onAutoRefresh(e.target.checked)}
          className="h-3.5 w-3.5 accent-brand-600"
        />
        <span
          className={
            'inline-block h-1.5 w-1.5 rounded-full ' +
            (autoRefresh ? 'animate-pulse bg-green-500' : 'bg-ink-300')
          }
        />
        Live
        {/* The timestamp is only shown when it is NOT refreshing. While it is
            live the number is always current, and a clock ticking beside it is
            noise; paused, "when was this?" is the whole question. */}
        {!autoRefresh && updatedAt ? (
          <span className="text-ink-400">
            · {new Date(updatedAt).toLocaleTimeString()}
          </span>
        ) : null}
      </label>

      <Select
        value={windowKey}
        onChange={(e) => onWindow(e.target.value as WindowKey)}
        className="w-44"
      >
        {WINDOW_OPTIONS.map((option) => (
          <option key={option.key} value={option.key}>
            {option.label}
          </option>
        ))}
      </Select>
    </div>
  );
}
